import {ApiRepository} from '../base/api-repository';
import {SessionRepository} from './session-repository';
import {action, makeObservable, observable} from 'mobx';
import {AzureSsoStore} from '../base/azure-sso-store';

type State = 'no-session' | 'weak-session' | 'strong-session' | 'admin-session';
export class SessionStore {

    @observable
    state: State = 'no-session';

    constructor(private sessionRepository: SessionRepository, private api: ApiRepository, private azureSsoStore: AzureSsoStore) {
        this.setState(this.sessionType());
        if (this.state === 'no-session') {
            sessionRepository.clearJwt();
        }
        makeObservable(this);
    }

    static parseJwtPayload(jwt: string): JwtPayload {
        return JSON.parse(atob(jwt.split('.')[1]));
    }

    static isExpired(jwtPayload: JwtPayload): boolean {
        return Date.now() >= jwtPayload.exp * 1000;
    }

    @action
    async externalTokenLogin(token?: string, tenantId?: string): Promise<void> {
        if (!token) {
            throw new Error(`Token is required for external token login.`);
        }

        if (SessionStore.isExpired(SessionStore.parseJwtPayload(token))) {
            throw new Error(`The provided token is expired.`);
        }

        if (tenantId) {
            this.azureSsoStore.setTenantId(tenantId);
        }

        const result = await this.api.post<SessionTokenResponse>(`/login/link-token`, { jwt: token });

        const sessionToken = result.jwt;

        if (sessionToken !== undefined) {
            this.sessionRepository.setJwt(sessionToken);
            this.setState(this.sessionType());
        } else {
            throw Error(`Unable to fetch the jwt for the link token ${token} or tenantId ${tenantId}`);
        }
    }

    @action
    async adminLogin(employeeEmailAddress: string, adminPassword: string): Promise<boolean> {
        const sessionToken = await this.api.post<SessionTokenResponse>(`/login/admin-password`, {employeeEmailAddress, adminPassword});

        if (!sessionToken.success) {
            return false;
        }

        this.sessionRepository.setJwt(sessionToken.jwt!);

        this.setState(this.sessionType());

        return true;
    }

    @action
    async logout(): Promise<void> {
        this.sessionRepository.clearJwt();

        if (this.azureSsoStore.getTenantId() !== undefined) {

            await this.azureSsoStore.ssoLogout();
        }
        this.setState('no-session');
    }

    @action
    async initiateEmailLogin(emailAddress: string, isMobileApp: boolean): Promise<InitiateLoginResponse> {
        return await this.api.post<InitiateLoginResponse>(`/login/initate-email`, {emailAddress, isMobileApp});
    }

    @action
    async initiatePhoneLogin(phone: string, isMobileApp: boolean): Promise<InitiateLoginResponse> {
        return await this.api.post<InitiateLoginResponse>(`/login/initate-mobile`, {phone, isMobileApp});
    }

    @action
    async completeOtpLogin(emailOrPhone: string, otpCode: string, isMobileApp: boolean): Promise<boolean> {
        const sessionToken = await this.api.post<SessionTokenResponse>(`/login/otp-code/complete`, {emailOrPhone, otpCode, isMobileApp});

        if (!sessionToken.success) {
            return false;
        }

        this.sessionRepository.setJwt(sessionToken.jwt!);
        this.setState(this.sessionType());

        return true;
    }

    @action
    async extendSession(): Promise<void> {
        const sessionToken = await this.api.post<SessionTokenResponse>('/login/extend');

        this.sessionRepository.setJwt(sessionToken.jwt!);
        this.setState(this.sessionType());
    }

    @action
    async extendSsoSession(tenantId: string): Promise<boolean> {
        return await this.createSsoSession(tenantId);
    }

    @action
    async createSsoSession(tenantId: string): Promise<boolean> {
        const sessionToken = await this.azureSsoStore.ssoLogin(tenantId);

        if (sessionToken.success) {
            this.sessionRepository.setJwt(sessionToken.jwt!);
            this.setState(this.sessionType());

            return true;
        } else {
            return false;
        }
    }

    getEmployeeId(): string {
        return this.getJwtPayload().employee;
    }

    getJwtPayload(): JwtPayload {
        return SessionStore.parseJwtPayload(this.sessionRepository.getJwt()!);
    }

    @action
    private setState(state: State): void {
        this.state = state;
    }

    private sessionType(): State {
        if (this.sessionRepository.getJwt() === undefined) {
            return 'no-session';
        }

        const payload = this.getJwtPayload();

        if (SessionStore.isExpired(payload)) {
            return 'no-session';
        }

        if (payload.type === 'admin-session') {
            return 'admin-session';
        }

        if (payload.type === 'link-token') {
            throw new Error('Link token not allowed in session repository');
        }

        return payload.type;
    }
}

interface SessionTokenResponse {
    jwt?: string,
    success: boolean
}

interface InitiateLoginResponse {
    success: boolean,
    tenantId?: string,
    failureReason?: 'otp-wait-period' | 'generic' | 'employee-not-found';
}

interface JwtPayload {
    type: 'weak-session' | 'strong-session' | 'admin-session' | 'link-token',
    employee: string,
    exp: number
}
